import React, { useEffect, useRef, useState } from "react";
import { Cosmograph, CosmographSearch, CosmographProvider } from "@cosmograph/react";
import styled, { useTheme } from "styled-components";
import { GraphNodeSubdomainEnum, GraphNodeTypeEnum } from "~/types/enums";
import { Box, Button, Card, InputToggle } from "~/components-shared";
const StyledCard = styled(Card)`
  position: absolute;
  backdrop-filter: blur(5px);
  background-color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.background.primary;
}}95;
  bottom: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.typography.fontStep * 10;
}}rem;
  left: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.typography.fontStep * 84;
}}rem;
`;
const StyledCosmographSearch = styled(CosmographSearch)`
  border-radius: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.shape.borderRadius.primary;
}};
  width: 320px;
  padding: 0 ${_ref5 => {
  let {
    theme
  } = _ref5;
  return theme.typography.fontStep * 2;
}}rem;
  background-color: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.colors.background.primary;
}}95;

  & > div {
    background-color: transparent !important;
  }

  .mdc-line-ripple {
    display: none;
  }

  .mdc-text-field {
    height: 50px;
  }
`;
const Wrapper = styled.div`
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
`;
export const edgeColors = (subdomain, type) => {
  const combo = `${subdomain}:${type}`;
  switch (combo) {
    case `${GraphNodeSubdomainEnum.PRODUCT}:${GraphNodeTypeEnum.DOCUMENT}`:
      return "#9f327a";
    case `${GraphNodeSubdomainEnum.PRODUCT}:${GraphNodeTypeEnum.TREND}`:
      return "#c55da3";
    case `${GraphNodeSubdomainEnum.PRODUCT}:${GraphNodeTypeEnum.TOPIC}`:
      return "#8e557b";
    case `${GraphNodeSubdomainEnum.PRODUCT}:${GraphNodeTypeEnum.THEME}`:
      return "#ca4ea2";
    case `${GraphNodeSubdomainEnum.PRODUCT}:${GraphNodeTypeEnum.ENTITY}`:
      return "#d86bb4";
    case `${GraphNodeSubdomainEnum.CUSTOMER}:${GraphNodeTypeEnum.DOCUMENT}`:
      return "#4f5dbd";
    case `${GraphNodeSubdomainEnum.CUSTOMER}:${GraphNodeTypeEnum.TREND}`:
      return "#737dc5";
    case `${GraphNodeSubdomainEnum.CUSTOMER}:${GraphNodeTypeEnum.TOPIC}`:
      return "#384187";
    case `${GraphNodeSubdomainEnum.CUSTOMER}:${GraphNodeTypeEnum.THEME}`:
      return "#1f2f9c";
    case `${GraphNodeSubdomainEnum.CUSTOMER}:${GraphNodeTypeEnum.ENTITY}`:
      return "#465ae3";
    case `:${GraphNodeTypeEnum.DOCUMENT}`:
      return "#407a69";
    case `:${GraphNodeTypeEnum.TREND}`:
      return "#3bc69c";
    case `:${GraphNodeTypeEnum.TOPIC}`:
      return "#295145";
    case `:${GraphNodeTypeEnum.THEME}`:
      return "#259573";
    case `:${GraphNodeTypeEnum.ENTITY}`:
      return "#66b69e";
    default:
      return "#66b69e";
  }
};
const KnowledgeGraphCanvas = _ref7 => {
  let {
    nodes,
    edges,
    createdNode,
    onNodeSelect,
    onNodeCreate
  } = _ref7;
  const {
    colors
  } = useTheme();
  const [showLabels, setShowLabels] = useState(false);
  const cosmographRef = useRef(null);
  const selection = nodes.filter(node => node.selected).map(node => ({
    id: node.id
  }));
  const handleSelect = node => {
    const ref = cosmographRef.current;
    if (ref) {
      if (node) {
        const nodeId = node.id;
        const selectedEdges = edges.filter(item => item.sourceId === nodeId || item.targetId === nodeId);
        ref.selectNodes([...selectedEdges.map(edge => ({
          id: edge.sourceId
        })), ...selectedEdges.map(edge => ({
          id: edge.targetId
        }))]);
        ref.zoomToNode({
          id: nodeId
        });
        onNodeSelect(node);
      } else {
        ref.unselectNodes();
        onNodeSelect(null);
      }
    }
  };
  useEffect(() => {
    if (createdNode) {
      setTimeout(() => {
        const ref = cosmographRef.current;
        ref.selectNodes([{
          id: createdNode.id
        }]);
        ref.zoomToNode({
          id: createdNode.id
        });
      }, 1000);
    }
  }, [createdNode]);
  const handleFilter = () => {
    const ref = cosmographRef.current;
    if (ref) {
      if (selection.length) {
        ref.selectNodes(selection);
      } else {
        ref.unselectNodes();
      }
    }
  };
  const onFiltered = filteredNodes => {
    if (!filteredNodes && selection.length) {
      handleFilter();
    }
  };
  useEffect(() => {
    handleFilter();
  }, [nodes, edges]);
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(CosmographProvider, {
    nodes: nodes,
    links: edges.map(edge => ({
      source: edge.sourceId,
      target: edge.targetId
    }))
  }, /*#__PURE__*/React.createElement(Cosmograph, {
    ref: cosmographRef,
    backgroundColor: "transparent",
    nodeColor: node => edgeColors(node.subdomain, node.type),
    hoveredNodeRingColor: colors.primary.main,
    focusedNodeRingColor: colors.primary.main,
    nodeGreyoutOpacity: 0.1,
    showHoveredNodeLabel: true,
    showDynamicLabels: showLabels,
    linkArrows: false,
    linkColor: "rgba(255,255,255,0.1)",
    linkGreyoutOpacity: 1,
    nodeLabelAccessor: node => node.name,
    nodeSize: node => node.totalScale,
    onClick: handleSelect,
    linkWidth: 1,
    linkWidthScale: 2,
    onNodesFiltered: onFiltered,
    nodeSizeScale: 0.5,
    curvedLinks: true,
    simulationGravity: 0.1,
    simulationRepulsion: 2,
    simulationRepulsionTheta: 1.15,
    simulationLinkDistance: 60,
    simulationFriction: 1
  }), /*#__PURE__*/React.createElement(StyledCard, {
    s: {
      p: 2
    }
  }, /*#__PURE__*/React.createElement(Box, {
    gap: 3,
    align: "center"
  }, /*#__PURE__*/React.createElement(StyledCosmographSearch, {
    openListUpwards: true,
    ordering: {
      include: ["name"]
    },
    accessors: [{
      label: "name",
      accessor: node => node.name
    }],
    onNodeSelect: handleSelect
  }), /*#__PURE__*/React.createElement(Button, {
    color: "translucentPrimary",
    onClick: onNodeCreate
  }, "Create a Node"), /*#__PURE__*/React.createElement(InputToggle, {
    value: showLabels,
    onChange: () => setShowLabels(p => !p),
    label: "Show Labels",
    s: {
      mr: 3
    }
  })))));
};
export default /*#__PURE__*/React.memo(KnowledgeGraphCanvas);